import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api, { getErrorMessage } from '@/hooks/useApi';

// Types matching backend responses
export type FreeTier = 'all' | 'narrator' | 'storyteller' | 'trailblazer';

export interface StoryNode {
  nodeId: string;
  storyId: string;
  parentNodeId: string | null;
  choiceIndex: number | null;
  depth: number;
  status: 'generating' | 'ready' | 'failed';
  isEnding?: boolean;
  isFree?: boolean;
  freeTier?: FreeTier;
  createdAt: string;
  choices?: Array<{ text: string; consequenceHint: string }>;
  childNodeIds?: { '0': string | null; '1': string | null; '2': string | null };
  storyState?: {
    chapterNumber: number;
    chapterTitle: string;
    genre: string;
    setting: string;
  };
}

interface ListNodesResponse {
  nodes: StoryNode[];
}

interface GetNodeResponse {
  node: StoryNode;
}

interface UpdateFreeTierResponse {
  nodeId: string;
  freeTier: FreeTier | null;
}

interface BatchUpdateResponse {
  updatedCount: number;
  requestedCount: number;
  freeTier: FreeTier | null;
}

// Query keys for cache management
export const nodeKeys = {
  all: ['nodes'] as const,
  lists: () => [...nodeKeys.all, 'list'] as const,
  listByStory: (storyId: string) => [...nodeKeys.lists(), { storyId }] as const,
  details: () => [...nodeKeys.all, 'detail'] as const,
  detail: (nodeId: string) => [...nodeKeys.details(), nodeId] as const,
};

/**
 * Fetch all nodes for a story (admin endpoint)
 */
export function useStoryNodes(storyId: string | undefined) {
  return useQuery({
    queryKey: nodeKeys.listByStory(storyId ?? ''),
    queryFn: async () => {
      const response = await api.get<ListNodesResponse>(`/admin/stories/${storyId}/nodes`);
      return response.data.nodes;
    },
    enabled: !!storyId,
  });
}

/**
 * Fetch a single node by ID (admin endpoint)
 */
export function useNode(nodeId: string | undefined) {
  return useQuery({
    queryKey: nodeKeys.detail(nodeId ?? ''),
    queryFn: async () => {
      const response = await api.get<GetNodeResponse>(`/admin/nodes/${nodeId}`);
      return response.data.node;
    },
    enabled: !!nodeId,
  });
}

/**
 * Update freeTier for a single node
 */
export function useUpdateNodeFreeTier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ nodeId, freeTier }: { nodeId: string; freeTier: FreeTier | null }) => {
      const response = await api.patch<UpdateFreeTierResponse>(`/admin/nodes/${nodeId}`, {
        freeTier,
      });
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate node detail cache
      queryClient.invalidateQueries({ queryKey: nodeKeys.detail(data.nodeId) });
      // Also invalidate list queries since freeTier changed
      queryClient.invalidateQueries({ queryKey: nodeKeys.lists() });
    },
    onError: (error) => {
      console.error('Update node freeTier failed:', getErrorMessage(error));
    },
  });
}

/**
 * Batch update freeTier for multiple nodes (path marking)
 */
export function useBatchUpdateFreeTier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ nodeIds, freeTier }: { nodeIds: string[]; freeTier: FreeTier | null }) => {
      const response = await api.post<BatchUpdateResponse>('/admin/nodes/batch-free-tier', {
        nodeIds,
        freeTier,
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all node queries since multiple nodes changed
      queryClient.invalidateQueries({ queryKey: nodeKeys.all });
    },
    onError: (error) => {
      console.error('Batch update freeTier failed:', getErrorMessage(error));
    },
  });
}

/**
 * Build a tree structure from a flat list of nodes
 */
export function buildNodeTree(nodes: StoryNode[]): Map<string, StoryNode[]> {
  const childrenMap = new Map<string, StoryNode[]>();

  // Group nodes by parent
  for (const node of nodes) {
    const parentId = node.parentNodeId ?? 'root';
    const children = childrenMap.get(parentId) ?? [];
    children.push(node);
    childrenMap.set(parentId, children);
  }

  // Sort children by choiceIndex
  for (const [, children] of childrenMap) {
    children.sort((a, b) => (a.choiceIndex ?? 0) - (b.choiceIndex ?? 0));
  }

  return childrenMap;
}

/**
 * Find the root node of a story
 */
export function findRootNode(nodes: StoryNode[]): StoryNode | undefined {
  return nodes.find((n) => n.parentNodeId === null);
}
